import { observer } from 'mobx-react-lite';
import { logUsage } from 'owa-analytics';
import { getCharm } from 'owa-calendar-events-store';
import CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import renderAsAllDay from 'owa-calendar-types/lib/utils/renderAsAllDay';
import { getLightColorScheme } from 'owa-color-utils/lib/colorUtils';
import {
    addMinutes,
    differenceInDays,
    differenceInMinutes,
    isEqual,
    OwaDate,
    owaDate,
} from 'owa-datetime';
import { DateRange } from 'owa-datetime-utils';
import * as React from 'react';
import {
    CalendarSurfaceItemsConfig,
    inDayItemTemplate,
    allDayItemTemplate,
    overflowItemTemplate,
    updateAllDayWellExpanded,
    isOverflowIndicatorId,
    SurfaceDecoratorProps,
    CalendarSurfaceTransformProps,
    SurfaceColumnItemsProvider,
    CalendarSurfaceStore,
    createItemsLayer,
    ColumnItemsProvider,
} from 'owa-calendar-surface-dategrid';
import { CalendarDayContent } from 'owa-calendar-surface-core';
import ComposeSchedulingInDayComponent from './ComposeSchedulingInDayComponent';
import ComposeSchedulingInDayContentComponent from './ComposeSchedulingInDayContentComponent';
import {
    getConfiguredEventStartTimeBasedOnPreviousDuration,
    getConfiguredEventStartTimeEndTimeBasedOnPreviousStartEnd,
    getConfiguredEventStartTime,
    getConfiguredEventEndTime,
} from 'owa-calendar-cloud-settings-utils';
import { isAttendeeItem, isSchedulingSelectionItem } from 'owa-day-grid-core-utilities';
import { getScenarioStore } from 'owa-day-grid-core-schema';
import { DayGrid, createDayGridConfig } from 'owa-day-grid-core';
import populateDayDataWithFreeBusy from '../utils/populateDayDataWithFreeBusy';

const selectionItemId = 'schedulingSelection';

/**
 * ComposeSchedulingGrid properties
 */
export interface ComposeSchedulingGridProps {
    scenarioId: string;
    selectionTemplate?: (store: any, model: CalendarEvent) => JSX.Element;
    conflictTemplate?: (store: any, model: CalendarEvent) => JSX.Element;
    onDateTimesChanged?: (start: OwaDate, end: OwaDate, isAllDay: boolean) => void;
    editMode?: boolean;
    shouldAdjustStartTimeEndTime?: boolean;
    showFreeBusyData?: boolean;
}

/**
 * Component for rendering scheduling grid in full compose form
 */
export default observer(function ComposeSchedulingGrid(props: ComposeSchedulingGridProps) {
    const viewState = getScenarioStore(props.scenarioId);

    /**
     * Calculates conflicts with other calendar events
     * @param model The selection surface model
     * @returns number of detected conflicts
     */
    const getConflictCount = (selection: DateRange) => {
        let result = 0;
        viewState.calendarItems.forEach(item => {
            if (
                item.ItemId.Id != selectionItemId &&
                item.FreeBusyType != undefined &&
                item.FreeBusyType != 'Free'
            ) {
                if (item.IsAllDayEvent) {
                    result++;
                } else {
                    if (selection.start < item.End && selection.end > item.Start) {
                        result++;
                    }
                }
            }
        });
        return result;
    };
    const onSurfaceClick = (
        start: OwaDate,
        end: OwaDate,
        isAllDay: boolean,
        isSelect?: boolean
    ) => {
        const event = viewState.selectionItem;
        const eventDuration = differenceInMinutes(event.End, event.Start);
        const eventDurationInDays = differenceInDays(event.End, event.Start);

        if (props.shouldAdjustStartTimeEndTime) {
            // Adjust startDate based on the shorten event duration setting
            start = isSelect
                ? getConfiguredEventStartTime(start, end)
                : getConfiguredEventStartTimeBasedOnPreviousDuration(eventDuration, start);
            end = isSelect ? getConfiguredEventEndTime(start, end) : end;
        }

        const surfaceDuration = differenceInMinutes(end, start);
        const endTime = addMinutes(
            start,
            isSelect || eventDurationInDays > 0 ? surfaceDuration : eventDuration
        );
        if (props.editMode) {
            props.onDateTimesChanged(
                owaDate(event.Start, start),
                owaDate(event.End, endTime),
                isAllDay
            );
        }
        // logs number of times user clicks on grid grouping by free / conflicting time
        logUsage('SchedulingGridClickEvent', {
            conflicts: getConflictCount({ start: start, end: endTime }) > 0,
        });
    };
    const onDateTimesChanged = (start: OwaDate, end: OwaDate) => {
        if (props.editMode) {
            // dateRange comes in the time zone of the grid, which is the event.start.tz.
            // Since we know events have have different tz in the start and end times, correct the end tz.
            const event = viewState.selectionItem;

            if (props.shouldAdjustStartTimeEndTime) {
                // Adjust startDate and endDate based on the shorten event duration setting
                if (!isEqual(start, event.Start) && !isEqual(end, event.End)) {
                    // The selection is being moved
                    const eventDuration = differenceInMinutes(event.End, event.Start);

                    start = getConfiguredEventStartTimeBasedOnPreviousDuration(
                        eventDuration,
                        start
                    );
                    end = addMinutes(start, eventDuration);
                } else {
                    // Drag the start time / end time
                    const configuredDateRange = getConfiguredEventStartTimeEndTimeBasedOnPreviousStartEnd(
                        event.Start,
                        event.End,
                        start,
                        end
                    );
                    start = configuredDateRange.start;
                    end = configuredDateRange.end;
                }
            }

            props.onDateTimesChanged(
                owaDate(event.Start.tz, start),
                owaDate(event.End.tz, end),
                false
            );
        }
    };
    const onOverflowItemClicked = (ev: React.MouseEvent<HTMLElement>) => {
        ev.stopPropagation();
        updateAllDayWellExpanded(viewState, true, 'ExpandButton');
    };
    let { selectionTemplate, conflictTemplate, editMode, scenarioId, showFreeBusyData } = props;
    let config = createDayGridConfig(viewState);

    // the compose grid doesn't show working hours or highlights
    config.disableWorkingHours = true;
    let calendarItemsConfig: CalendarSurfaceItemsConfig = {
        itemsEventHandlers: null,
        itemsDecorator: itemsDecorator,
        template: (store, model, state) =>
            inDayItemTemplate(
                store,
                model,
                getLightColorScheme(),
                editMode && getCharm(model),
                state,
                false,
                false
            ),
    };
    const calendarAllDayDayItemsConfig: CalendarSurfaceItemsConfig = {
        itemsEventHandlers: null,
        itemsDecorator: itemsDecorator,
        template: (store, model, state) =>
            allDayItemTemplate(
                store,
                model,
                getLightColorScheme(),
                editMode && getCharm(model),
                state
            ),
    };
    const attendeeItemsConfig: CalendarSurfaceItemsConfig = {
        itemsEventHandlers: null,
        itemsDecorator: itemsDecorator,
        template: conflictTemplate,
    };
    const suggestedItemsConfig: CalendarSurfaceItemsConfig = {
        itemsEventHandlers: null,
        itemsDecorator: selectionDecorator,
        template: selectionTemplate,
    };
    const overflowItemsConfig: CalendarSurfaceItemsConfig = {
        itemsEventHandlers: {
            onClick: onOverflowItemClicked,
        },
        itemsDecorator: selectionDecorator,
        template: overflowItemTemplate,
    };
    config.getItemConfig = model => {
        if (isOverflowIndicatorId(model.ItemId.Id)) {
            return overflowItemsConfig;
        }
        if (isAttendeeItem(model)) {
            return attendeeItemsConfig;
        }
        if (isSchedulingSelectionItem(model)) {
            return suggestedItemsConfig;
        }
        return renderAsAllDay(model) ? calendarAllDayDayItemsConfig : calendarItemsConfig;
    };
    config.onSurfaceClick = editMode ? onSurfaceClick : () => null;

    const getCalendarDayContentConfig = React.useCallback(
        (content: CalendarDayContent, calendarConfig: CalendarSurfaceTransformProps) => ({
            inDayComponent: {
                component: ComposeSchedulingInDayComponent,
                additionalProps: {
                    scenarioId: scenarioId,
                },
            },
            inDayContentComponent: {
                component: ComposeSchedulingInDayContentComponent,
                additionalProps: {
                    scenarioId: scenarioId,
                    config: config,
                    shouldAdjustStartTimeEndTime: props.shouldAdjustStartTimeEndTime,
                    onDateTimesChanged: onDateTimesChanged,
                },
            },
        }),
        [scenarioId, viewState]
    );

    return (
        <DayGrid
            scenarioId={scenarioId}
            config={config}
            getCalendarDayContentConfig={getCalendarDayContentConfig}
            highlightedEventId={selectionItemId}
            customCalendarDayContentGenerator={customCalendarDayContentGenerator}
            customPopulateDayData={showFreeBusyData ? populateDayDataWithFreeBusy : undefined}
            customColumnContent={customColumnContent}
            customItemFilter={customItemFilter}
        />
    );
});

function itemsDecorator({ item, style, ...divProps }: SurfaceDecoratorProps<unknown>) {
    return <div {...divProps} style={{ ...style, position: 'absolute' }} />;
}

function selectionDecorator({ item, style, ...divProps }: SurfaceDecoratorProps<unknown>) {
    return <div {...divProps} style={{ ...style, position: 'absolute', zIndex: 3 }} />;
}

function isColumnItem(item: CalendarEvent) {
    return !isSchedulingSelectionItem(item) && !isAttendeeItem(item);
}
function customItemFilter(item: CalendarEvent) {
    return isColumnItem(item) && item.ResponseType !== 'Decline';
}

function customColumnContent(
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    index: number
) {
    const freeBusyItemsProvider = new SurfaceColumnItemsProvider(
        store,
        config.columnProvider,
        () =>
            store.calendarItems.filter(
                item => !isColumnItem(item) && item.ResponseType !== 'Decline'
            ),
        config.supportExpandableAllDayWell()
    );
    return createItemsLayer(freeBusyItemsProvider.columnInDayItems[index], store, config, index);
}

function customCalendarDayContentGenerator(
    store: CalendarSurfaceStore,
    config: CalendarSurfaceTransformProps,
    columnItemsProvider: ColumnItemsProvider
) {
    return {
        tabOrderProvider: () => [...columnItemsProvider.tabOrder, 'schedulingSelection'],
        initialTabId: 'schedulingSelection',
    };
}
